// =============================================================================
// Phase 1 SOC / Wazuh Integration: Zustand Store
// =============================================================================

import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import type { 
  WazuhConfig, 
  DiscoveredIdentity, 
  DiscoveryFilters,
  SyncResult 
} from '@/types/security';

interface SecurityState {
  // Wazuh config
  wazuhConfig: WazuhConfig | null;
  configLoading: boolean;
  configError: string | null;
  
  // Discoveries
  discoveries: DiscoveredIdentity[];
  discoveriesLoading: boolean;
  discoveriesError: string | null;
  filters: DiscoveryFilters;
  
  // Sync state
  syncing: boolean;
  lastSyncResult: SyncResult | null;
  
  // Actions
  fetchWazuhConfig: (orgId: string) => Promise<void>;
  saveWazuhConfig: (orgId: string, config: Partial<WazuhConfig>) => Promise<void>;
  fetchDiscoveries: (orgId: string, filters?: DiscoveryFilters) => Promise<void>;
  setFilters: (filters: DiscoveryFilters) => void;
  triggerSync: (orgId: string) => Promise<SyncResult>;
  linkDiscovery: (discoveryId: string, endpointId: string) => Promise<void>;
  ignoreDiscovery: (discoveryId: string) => Promise<void>;
  createEndpointFromDiscovery: (discoveryId: string, endpointData: {
    name: string;
    type: string;
    system_id?: string;
    site_id?: string;
  }) => Promise<string>;
}

export const useSecurityStore = create<SecurityState>((set, get) => ({
  // Initial state
  wazuhConfig: null,
  configLoading: false,
  configError: null,
  
  discoveries: [],
  discoveriesLoading: false,
  discoveriesError: null,
  filters: {},
  
  syncing: false,
  lastSyncResult: null,
  
  // Fetch Wazuh config for org
  fetchWazuhConfig: async (orgId: string) => {
    set({ configLoading: true, configError: null });
    try {
      const { data, error } = await supabase
        .from('wazuh_configs')
        .select('*')
        .eq('org_id', orgId)
        .maybeSingle();
      
      if (error) throw error;
      set({ wazuhConfig: data as WazuhConfig | null, configLoading: false });
    } catch (err) {
      set({ 
        configError: err instanceof Error ? err.message : 'Failed to fetch Wazuh config',
        configLoading: false 
      });
    }
  },
  
  // Save or update Wazuh config
  saveWazuhConfig: async (orgId: string, config: Partial<WazuhConfig>) => {
    set({ configLoading: true, configError: null });
    try {
      const existing = get().wazuhConfig;
      
      if (existing) {
        // Update existing config
        const { error } = await supabase
          .from('wazuh_configs')
          .update({
            ...config,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id);
        
        if (error) throw error;
      } else {
        // Create new config
        const { error } = await supabase
          .from('wazuh_configs')
          .insert({
            org_id: orgId,
            api_url: config.api_url || '',
            api_user: config.api_user || '',
            api_password_encrypted: config.api_password_encrypted || '',
            verify_ssl: config.verify_ssl ?? true,
            sync_enabled: config.sync_enabled ?? false,
            sync_interval_minutes: config.sync_interval_minutes ?? 15,
            online_threshold_minutes: config.online_threshold_minutes ?? 5,
            default_site_id: config.default_site_id || null,
          });
        
        if (error) throw error;
      }
      
      // Refresh config
      await get().fetchWazuhConfig(orgId);
    } catch (err) {
      set({ 
        configError: err instanceof Error ? err.message : 'Failed to save Wazuh config',
        configLoading: false 
      });
      throw err;
    }
  },
  
  // Fetch discovered identities
  fetchDiscoveries: async (orgId: string, filters?: DiscoveryFilters) => {
    set({ discoveriesLoading: true, discoveriesError: null });
    try {
      let query = supabase
        .from('discovered_identities')
        .select(`
          *,
          site:sites(id, name),
          endpoint:endpoints(id, name, type)
        `)
        .eq('org_id', orgId)
        .order('last_seen_at', { ascending: false });
      
      // Apply filters
      if (filters?.site_id) {
        query = query.eq('site_id', filters.site_id);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.search) {
        query = query.or(`ip.ilike.%${filters.search}%,mac.ilike.%${filters.search}%,hostname.ilike.%${filters.search}%`);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      // Compute connected status based on config threshold
      const config = get().wazuhConfig;
      const thresholdMinutes = config?.online_threshold_minutes ?? 5;
      const now = new Date();
      
      const discoveriesWithStatus = (data || []).map((d) => ({
        ...d,
        connected: d.last_seen_at 
          ? (now.getTime() - new Date(d.last_seen_at).getTime()) / 60000 <= thresholdMinutes
          : false,
      })) as DiscoveredIdentity[];
      
      // Apply connected filter if specified
      let filteredDiscoveries = discoveriesWithStatus;
      if (filters?.connected !== undefined) {
        filteredDiscoveries = discoveriesWithStatus.filter(d => d.connected === filters.connected);
      }
      
      set({ 
        discoveries: filteredDiscoveries, 
        discoveriesLoading: false,
        filters: filters || {},
      });
    } catch (err) {
      set({ 
        discoveriesError: err instanceof Error ? err.message : 'Failed to fetch discoveries',
        discoveriesLoading: false 
      });
    }
  },
  
  // Set filters
  setFilters: (filters: DiscoveryFilters) => {
    set({ filters });
  },
  
  // Trigger manual sync via Edge Function
  triggerSync: async (orgId: string): Promise<SyncResult> => {
    set({ syncing: true });
    try {
      const { data, error } = await supabase.functions.invoke('wazuh-sync', {
        body: { org_id: orgId },
      });
      
      if (error) throw error;
      
      const result = data as SyncResult;
      set({ syncing: false, lastSyncResult: result });
      
      // Refresh discoveries after sync
      await get().fetchDiscoveries(orgId, get().filters);
      
      return result;
    } catch (err) {
      const errorResult: SyncResult = {
        success: false,
        discoveries_created: 0,
        discoveries_updated: 0,
        errors: [err instanceof Error ? err.message : 'Sync failed'],
        sync_timestamp: new Date().toISOString(),
      };
      set({ syncing: false, lastSyncResult: errorResult });
      throw err;
    }
  },
  
  // Link discovery to existing endpoint
  linkDiscovery: async (discoveryId: string, endpointId: string) => {
    try {
      const { error } = await supabase.functions.invoke('wazuh-discovery', {
        body: { 
          action: 'link',
          discovery_id: discoveryId,
          endpoint_id: endpointId,
        },
      });
      
      if (error) throw error;
      
      // Update local state
      set((state) => ({
        discoveries: state.discoveries.map((d) =>
          d.id === discoveryId
            ? { ...d, endpoint_id: endpointId, status: 'linked' as const }
            : d
        ),
      }));
    } catch (err) {
      throw err;
    }
  },
  
  // Mark discovery as ignored
  ignoreDiscovery: async (discoveryId: string) => {
    try {
      const { error } = await supabase.functions.invoke('wazuh-discovery', {
        body: { 
          action: 'ignore',
          discovery_id: discoveryId,
        },
      });
      
      if (error) throw error;
      
      // Update local state
      set((state) => ({
        discoveries: state.discoveries.map((d) =>
          d.id === discoveryId
            ? { ...d, status: 'ignored' as const }
            : d
        ),
      }));
    } catch (err) {
      throw err;
    }
  },
  
  // Create new endpoint from discovery
  createEndpointFromDiscovery: async (discoveryId: string, endpointData: {
    name: string;
    type: string;
    system_id?: string;
    site_id?: string;
  }): Promise<string> => {
    try {
      const { data, error } = await supabase.functions.invoke('wazuh-discovery', {
        body: { 
          action: 'create-endpoint',
          discovery_id: discoveryId,
          endpoint_data: endpointData,
        },
      });
      
      if (error) throw error;
      
      const endpointId = data.endpoint_id as string;
      
      // Update local state
      set((state) => ({
        discoveries: state.discoveries.map((d) =>
          d.id === discoveryId
            ? { ...d, endpoint_id: endpointId, status: 'linked' as const }
            : d
        ),
      }));
      
      return endpointId;
    } catch (err) {
      throw err;
    }
  },
}));
